import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Tooltip,
  alpha,
  useTheme,
  Paper,
  LinearProgress,
  Skeleton,
} from "@mui/material";
import {
  FiCreditCard,
  FiZap,
  FiStar,
  FiTrendingUp,
  FiCheck,
  FiExternalLink,
  FiDownload,
  FiShield,
  FiCpu,
  FiAward,
} from "react-icons/fi";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import useStyles from "./styles";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import { CustomButtonTable } from "../../components/CustomButtonTable/CustomButtonTable";
import { CustomModal } from "../../components/CustomModal/CustomModal";
import { getCurrentFleetId } from "../../utils/getCurrentFleet";
import { usePermissions } from "../../contexts/PermissionContext";
import {
  useGetPlans,
  useGetSubscription,
  useGetBillingConfig,
  useCreateCheckoutSession,
  useCreatePortalSession,
  useCancelSubscription,
  useResumeSubscription,
  BillingCycle,
  Plan,
  ValidatedPromoCode,
} from "../../api/billing";
import { useGetAllVehiculesFleet } from "../../api/vehicule";
import PromoCodeInput from "./components/PromoCodeInput";
import {
  addonPerMonthCents,
  addonTotalCents,
  addonYearlyCents,
  formatMoney,
  saasPerVehicleCents,
  saasTotalCents,
} from "./pricing";

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Slugs this build ships presentation metadata for.
 *
 * These are NOT the set of plans that exist: plans are administrator-managed
 * rows, so a slug is just a string as far as this page is concerned. The
 * literal unions below exist only to type the metadata lookup tables, which
 * every consumer must treat as best-effort with a default fallback.
 */
type SaasPlanId = "essential" | "performance" | "optimization";
type AiAddonId = "ai_assistant" | "fleet_copilot" | "autonomous";

/** A plan slug coming from the API. Deliberately open. */
type PlanSlug = string;

const SAAS_PLAN_META: Record<
  SaasPlanId,
  { icon: React.ReactNode; colorKey: "info" | "success" | "warning"; featured?: boolean }
> = {
  essential: { icon: <FiZap size={20} />, colorKey: "info" },
  performance: { icon: <FiTrendingUp size={20} />, colorKey: "success", featured: true },
  optimization: { icon: <FiStar size={20} />, colorKey: "warning" },
};

/**
 * Presentation defaults for a plan slug this build has never heard of.
 *
 * The admin can create, rename and reprice plans at runtime
 * (BillingAdminController). Anything keyed on a slug literal here is therefore
 * decoration only - it must never decide whether a plan is shown, or a plan
 * added by an administrator would be invisible to every customer until the
 * frontend is redeployed.
 */
const DEFAULT_SAAS_META = {
  icon: <FiZap size={20} />,
  colorKey: "info" as const,
  featured: false,
};

const DEFAULT_ADDON_META = {
  icon: <FiCpu size={18} />,
  colorKey: "info" as const,
};

const AI_ADDON_META: Record<AiAddonId, { icon: React.ReactNode; colorKey: "info" | "success" | "warning" }> = {
  ai_assistant: { icon: <FiCpu size={18} />, colorKey: "info" },
  fleet_copilot: { icon: <FiShield size={18} />, colorKey: "success" },
  autonomous: { icon: <FiAward size={18} />, colorKey: "warning" },
};

const ADDON_LABEL_KEY: Record<AiAddonId, string> = {
  ai_assistant: "aiAssistant",
  fleet_copilot: "fleetCopilot",
  autonomous: "autonomousFleet",
};

// ─── Component ───────────────────────────────────────────────────────────────

export const Billing: React.FC = () => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const companyId = getCurrentFleetId();

  /*
   * Subscribing, changing plan, cancelling and opening the Stripe portal all
   * move money for the whole company. The API gates them on the fleet-manager
   * role; mirroring that here means a collaborator sees the plan catalogue
   * (useful context) without being handed buttons that will 403 - or worse,
   * that they assume they are expected to press.
   */
  const { can } = usePermissions();
  const canManageBilling = can("fleet.billing.manage");

  // Monthly is always the first thing shown. Prices are quoted per
  // vehicle/month everywhere, so opening on Annual made the headline figure
  // and the total underneath describe two different periods — the customer
  // had to notice the toggle to know which one they were reading. Annual
  // stays one click away, nudged by the "Save with annual" chip below.
  const [billing, setBilling] = useState<BillingCycle>("monthly");
  const [vehicleCount, setVehicleCount] = useState(10);
  const [selectedPlan, setSelectedPlan] = useState<PlanSlug | null>(null);
  const [selectedAddon, setSelectedAddon] = useState<PlanSlug | null>(null);
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  // Track whether the user has manually adjusted the vehicle count so we don't
  // overwrite their choice once real fleet data arrives.
  const [vehicleCountTouched, setVehicleCountTouched] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  // Real fleet size, used to pre-fill the vehicle quantity at checkout.
  const { totalCount: fleetVehicleCount } = useGetAllVehiculesFleet(companyId, 1, 1);

  const { plans: saasPlansRaw, isPending: plansLoading } = useGetPlans("saas");
  const { plans: addonPlansRaw } = useGetPlans("ai_addon");
  const { subscription, isPending: subLoading, isError: subError } =
    useGetSubscription(companyId);

  /*
   * What the active payment provider can actually do.
   *
   * Stripe and Revolut are not feature-equivalent: Revolut has no hosted
   * billing portal, no invoice PDFs and no coupon object. Rendering those
   * controls regardless would give the customer buttons that fail, so every
   * one of them is gated on a capability the API reports rather than on an
   * assumption baked in here.
   *
   * Note this describes the provider that will handle the NEXT checkout, which
   * for an existing subscriber may not be the one billing them today — see
   * `subscriptionProviderLabel` below.
   */
  const { config: billingConfig } = useGetBillingConfig();
  const capabilities = billingConfig.capabilities;

  const PROVIDER_LABELS: Record<string, string> = {
    stripe: "Stripe",
    revolut: "Revolut",
  };

  /*
   * Who bills THIS subscription.
   *
   * A company that subscribed through Stripe keeps being billed by Stripe even
   * after an admin points new checkouts at Revolut — the provider is fixed per
   * subscription for its whole life. So the "Managed by" label reads the
   * subscription's own provider and only falls back to the active one when
   * there is no subscription yet.
   */
  const subscriptionProvider =
    (subscription?.provider as string | undefined) ?? billingConfig.provider;
  const subscriptionProviderLabel =
    PROVIDER_LABELS[subscriptionProvider] ?? subscriptionProvider;

  const checkoutMutation = useCreateCheckoutSession(companyId);
  const portalMutation = useCreatePortalSession(companyId);
  const cancelMutation = useCancelSubscription(companyId);
  const resumeMutation = useResumeSubscription(companyId);

  /*
   * Promo code, only where the provider expects us to collect it.
   *
   * Stripe's hosted page has its own promo field, so a second one here would
   * let a customer apply one code in our form and another on Stripe's, with
   * nothing to reconcile the two. Revolut's page has none, so without this the
   * discount would be unreachable.
   */
  const collectPromoHere =
    billingConfig.promo_enabled &&
    capabilities.promo_collected_by === "app";

  const [promo, setPromo] = useState<ValidatedPromoCode | null>(null);

  // Codes can be plan-scoped, so a code validated against one plan must not
  // survive a switch to another and show a discount checkout would refuse.
  useEffect(() => {
    if (promo) setPromo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan, selectedAddon, billing]);

  const colorFor = (key: "info" | "success" | "warning") => theme.palette[key].main;

  // Pre-fill the vehicle quantity from the active subscription (if any) or the
  // company's real fleet size, once, and only until the user changes it.
  useEffect(() => {
    if (vehicleCountTouched) return;
    const seeded = subscription?.vehicle_quantity ?? fleetVehicleCount;
    if (seeded && seeded > 0) {
      setVehicleCount(seeded);
    }
  }, [subscription?.vehicle_quantity, fleetVehicleCount, vehicleCountTouched]);

  // Merge live plan data (price, features) with local presentation metadata.
  //
  // BUGFIX: this used to filter on a hard-coded slug whitelist
  // (`["essential", "performance", "optimization"].includes(p.slug)`). Plans
  // are administrator-managed data, so any plan created in the admin - or any
  // slug rename - was silently dropped here and never offered to customers,
  // with no error anywhere to explain the omission. The API already returns
  // only active plans of the requested tier; that is the correct filter.
  const saasPlans = useMemo(() => {
    return (saasPlansRaw as Plan[])
      .slice()
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((plan) => ({
        plan,
        ...DEFAULT_SAAS_META,
        ...(SAAS_PLAN_META[plan.slug as SaasPlanId] ?? {}),
      }));
  }, [saasPlansRaw]);

  const aiAddons = useMemo(() => {
    return (addonPlansRaw as Plan[])
      .slice()
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((plan) => ({
        plan,
        ...DEFAULT_ADDON_META,
        ...(AI_ADDON_META[plan.slug as AiAddonId] ?? {}),
      }));
  }, [addonPlansRaw]);

  const activePlanSlug = subscription?.plan?.slug as PlanSlug | undefined;
  const activeAddonSlug =
    (subscription?.metadata?.ai_addon_slug as PlanSlug | undefined) || null;

  const currentPlanForCheckout = selectedPlan ?? activePlanSlug ?? "performance";

  // ── Pricing ──
  // All money maths lives in ./pricing.ts so this page and the signup wizard
  // quote identical figures. Do not reintroduce local price helpers here:
  // the two surfaces drifting apart is exactly the bug that module prevents.
  const money = (valueCents: number, currency = "EUR") =>
    formatMoney(valueCents, currency, i18n.language);

  // Invoice amounts arrive from the API already converted to major units
  // (SubscriptionInvoice::getTotalAttribute divides by 100), unlike plan
  // prices which are in cents. Re-multiply so both go through formatMoney.
  const formatCurrency = (valueMajorUnits: number, currency = "EUR") =>
    formatMoney(Math.round(valueMajorUnits * 100), currency, i18n.language);

  // ── Actions ──
  const handleManagePortal = async () => {
    if (!canManageBilling) return;
    setPendingAction("portal");
    try {
      const { url } = await portalMutation.mutateAsync();
      if (url) window.location.href = url;
    } finally {
      setPendingAction(null);
    }
  };

  // Handlers are guarded too: a disabled button is a UI affordance, not a
  // control-flow guarantee (stale render, keyboard, devtools).
  const handleCheckout = async () => {
    if (!canManageBilling) return;
    setPendingAction("checkout");
    try {
      const { url } = await checkoutMutation.mutateAsync({
        plan_slug: currentPlanForCheckout,
        billing_cycle: billing,
        vehicle_quantity: vehicleCount,
        ai_addon_slug: selectedAddon,
        // Sent only when this provider expects it; on Stripe the code travels
        // with the customer to the hosted page instead.
        promo_code: collectPromoHere ? promo?.code ?? null : null,
      });
      if (url) window.location.href = url;
    } finally {
      setPendingAction(null);
    }
  };

  const handleCancel = async () => {
    if (!canManageBilling) return;
    if (!subscription) return;
    setPendingAction("cancel");
    try {
      await cancelMutation.mutateAsync({ subscriptionId: subscription.id });
      setConfirmCancelOpen(false);
    } finally {
      setPendingAction(null);
    }
  };

  const handleResume = async () => {
    if (!canManageBilling) return;
    if (!subscription) return;
    setPendingAction("resume");
    try {
      await resumeMutation.mutateAsync(subscription.id);
    } finally {
      setPendingAction(null);
    }
  };

  const usagePct = subscription
    ? Math.min(Math.round((subscription.vehicle_quantity / (vehicleCount || 1)) * 100), 100)
    : 0;

  return (
    <Sidebar styles={{ sidebarRightRoot: classes.billingRoot, sidebarRoot: classes.contentMain }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* ── Header ── */}
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2}>
          <Box>
            <CustomTypography variant="h2" text={t("billing.billing", "Billing & Subscription")} isUppercase />
            <CustomTypography variant="default" text={t("billing.subtitle", "Manage your plan, usage and invoices")} />
          </Box>
          {/* Portal is Stripe-only. Revolut has no hosted self-service page,
              and the API answers 501 — so the button is hidden rather than
              rendered into a dead end. Cancel, resume and plan changes all
              remain available in-app below, which is the whole reason hiding
              this is acceptable. */}
          {subscription?.metadata && capabilities.billing_portal && (
            <CustomButtonTable
              variant="primary"
              textVariant="link"
              label={t("billing.managePortal", "Manage billing portal")}
              textTransform="uppercase"
              width="fit-content"
              fontWeight="bold"
              textDecoration="none"
              iconName="external-link"
              disabled={pendingAction === "portal" || !canManageBilling}
              onClick={handleManagePortal}
            />
          )}
        </Stack>

        {subError && (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: "error.main" }}>
            <Typography color="error.main" fontWeight={600}>
              {t("billing.errorLoad", "Could not load billing information.")}
            </Typography>
          </Paper>
        )}

        {/* ── Current subscription summary ── */}
        {subLoading ? (
          <Skeleton variant="rounded" height={180} sx={{ borderRadius: 3 }} />
        ) : subscription ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} mb={2} flexWrap="wrap">
                  <FiCreditCard size={18} />
                  <Typography fontWeight={700}>{t("billing.currentPlan", "Current plan")}</Typography>
                  <Chip
                    label={t(
                      subscription.on_trial
                        ? "billing.onTrial"
                        : subscription.status === "active" || subscription.status === "trialing"
                        ? "billing.active"
                        : `billing.status.${subscription.status}`,
                      subscription.status
                    )}
                    size="small"
                    sx={{
                      bgcolor: alpha(
                        subscription.is_active ? theme.palette.success.main : theme.palette.warning.main,
                        0.12
                      ),
                      color: subscription.is_active ? "success.main" : "warning.main",
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={t(`billing.${subscription.plan.slug}`, subscription.plan.name)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  {subscription.on_grace_period && (
                    <Chip
                      label={t("billing.endsOn", "Ends {{date}}", {
                        date: subscription.ends_at ? new Date(subscription.ends_at).toLocaleDateString() : "",
                      })}
                      size="small"
                      sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: "error.main", fontWeight: 700 }}
                    />
                  )}
                </Stack>
                <Grid container spacing={2}>
                  {[
                    { label: t("billing.plan", "Plan"), value: t(`billing.${subscription.plan.slug}`, subscription.plan.name) },
                    { label: t("billing.billingCycle", "Billing cycle"), value: t(`billing.${subscription.billing_cycle}`, subscription.billing_cycle) },
                    { label: t("billing.vehicles", "Vehicles"), value: `${subscription.vehicle_quantity}` },
                    {
                      label: t("billing.nextRenewal", "Next renewal"),
                      value: subscription.current_period_end
                        ? new Date(subscription.current_period_end).toLocaleDateString()
                        : "—",
                    },
                  ].map(({ label, value }) => (
                    <Grid item xs={6} md={3} key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography fontWeight={700} fontSize={14}>{value}</Typography>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  {t("billing.vehiclesUsed", "Vehicles used")} — {subscription.vehicle_quantity} / {vehicleCount || subscription.vehicle_quantity}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={usagePct}
                  sx={{ height: 8, borderRadius: 4, bgcolor: alpha(theme.palette.primary.main, 0.12) }}
                />
                <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t("billing.managedByProvider", "Managed by {{provider}}", {
                      provider: subscriptionProviderLabel,
                    })}
                  </Typography>
                  {subscription.on_grace_period ? (
                    // Resume only exists on Stripe: a cancelled Revolut
                    // subscription is terminal and reactivating means a new
                    // checkout. Showing the button anyway would surface the
                    // API's refusal as an error the customer cannot act on.
                    subscriptionProvider === "stripe" ? (
                      <CustomButtonTable
                        variant="secondary"
                        textVariant="link"
                        label={t("billing.resume", "Resume subscription")}
                        textTransform="uppercase"
                        width="fit-content"
                        fontWeight="bold"
                        textDecoration="none"
                        disabled={pendingAction === "resume" || !canManageBilling}
                        onClick={handleResume}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {t(
                          "billing.resumeUnavailable",
                          "To reactivate, choose a plan below."
                        )}
                      </Typography>
                    )
                  ) : subscription.is_active ? (
                    <Box
                      component="span"
                      onClick={() => canManageBilling && setConfirmCancelOpen(true)}
                      sx={{
                        fontSize: 12,
                        color: "text.disabled",
                        cursor: canManageBilling ? "pointer" : "not-allowed",
                        opacity: canManageBilling ? 1 : 0.5,
                        "&:hover": { color: canManageBilling ? "error.main" : "text.disabled" },
                      }}
                    >
                      {t("billing.cancelAtPeriodEnd", "Cancel at period end")}
                    </Box>
                  ) : null}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", gap: 1.5 }}>
                <Typography fontWeight={700}>{t("billing.upgradePrompt", "Need more features?")}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("billing.aiLayer", "AI Layer")} — {t("billing.addonOptional", "Optional add-on")}
                </Typography>
                <CustomButtonTable
                  variant="primary"
                  textVariant="link"
                  label={t("billing.upgrade", "Upgrade plan")}
                  textTransform="uppercase"
                  width="fit-content"
                  fontWeight="bold"
                  textDecoration="none"
                  iconName="plus"
                  onClick={() => document.getElementById("plans-section")?.scrollIntoView({ behavior: "smooth" })}
                />
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
            <Typography fontWeight={700} mb={0.5}>
              {t("billing.noSubscription", "No active subscription yet")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("billing.noSubscriptionSub", "Choose a plan below to get started.")}
            </Typography>
          </Paper>
        )}

        {/* ── Recent invoices ── */}
        {!!subscription?.invoices?.length && (
          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography fontWeight={700}>{t("billing.invoices", "Recent invoices")}</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.03) }}>
                  {[t("billing.invoiceDate"), "ID", t("billing.invoiceAmount"), t("billing.invoiceStatus"), ...(capabilities.hosted_invoices ? [""] : [])].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {subscription.invoices.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ fontSize: 13 }}>
                      {inv.period_start ? new Date(inv.period_start).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: "text.secondary" }}>{inv.number ?? `#${inv.id}`}</TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 700 }}>
                      {formatCurrency(inv.total, inv.currency?.toUpperCase())}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={inv.status === "paid" ? t("billing.invoicePaid", "Paid") : t("billing.invoicePending", "Pending")}
                        size="small"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: inv.status === "paid"
                            ? alpha(theme.palette.success.main, 0.12)
                            : alpha(theme.palette.warning.main, 0.12),
                          color: inv.status === "paid" ? "success.main" : "warning.main",
                        }}
                      />
                    </TableCell>
                    {/* The download column exists only where the provider
                        issues invoice documents. Revolut records orders with no
                        PDF, so the column is dropped entirely rather than shown
                        permanently empty — a blank action column reads as a
                        broken feature, not an absent one. */}
                    {capabilities.hosted_invoices && (
                      <TableCell>
                        {inv.pdf_url && (
                          <Tooltip title={t("billing.download", "Download")}>
                            <Box component="span" sx={{ cursor: "pointer", color: "text.secondary", display: "inline-flex" }}
                              onClick={() => window.open(inv.pdf_url ?? "#", "_blank")}
                            >
                              <FiDownload size={15} />
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        {/* ── Plans section ── */}
        <Box id="plans-section">
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2} mb={3}>
            <Typography variant="h6" fontWeight={700}>{t("billing.plans", "Available plans")}</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <ToggleButtonGroup
                size="small"
                value={billing}
                exclusive
                onChange={(_, v) => v && setBilling(v)}
                sx={{ "& .MuiToggleButton-root": { fontSize: 12, px: 2 } }}
              >
                {/* Monthly first: it is the default cycle and the one the
                    per-vehicle headline price is expressed in. Same order as
                    PlanSelector, so the signup wizard and this page do not
                    present the choice back-to-front from each other. */}
                <ToggleButton value="monthly">{t("billing.monthly", "Monthly")}</ToggleButton>
                <ToggleButton value="annual">{t("billing.annual", "Annual")}</ToggleButton>
              </ToggleButtonGroup>
              {billing === "monthly" && (
                // Incentive to switch: only shown while on Monthly, so it nudges
                // the user toward annual billing (matching the public pricing page).
                <Chip label={t("billing.saveAnnual", "Save with annual")} size="small" color="success" sx={{ fontSize: 11 }} />
              )}
            </Stack>
          </Stack>

          {/* Vehicle slider */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography fontWeight={600} fontSize={14}>{t("billing.vehicleCount", "Number of vehicles")}</Typography>
              <Typography fontWeight={700} color="primary">{vehicleCount}</Typography>
            </Stack>
            <Slider
              min={1} max={500} step={1}
              value={vehicleCount}
              onChange={(_, v) => { setVehicleCount(v as number); setVehicleCountTouched(true); }}
              marks={[{ value: 1, label: "1" }, { value: 100, label: "100" }, { value: 250, label: "250" }, { value: 500, label: "500" }]}
              sx={{ mt: 1 }}
            />
          </Paper>

          {/* SaaS plan cards */}
          {plansLoading ? (
            <Grid container spacing={2}>
              {[0, 1, 2].map((i) => (
                <Grid item xs={12} md={4} key={i}>
                  <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={2}>
              {saasPlans.map(({ plan, icon, colorKey, featured }) => {
                const color = colorFor(colorKey);
                const isActive = activePlanSlug === plan.slug && !selectedPlan;
                const isSelected = selectedPlan === plan.slug;
                const perVehicleCents = saasPerVehicleCents(plan, billing);
                // Total reflects the selected billing cycle: a yearly estimate
                // for annual, the monthly bill for monthly.
                const totalCents = saasTotalCents(plan, billing, vehicleCount);

                return (
                  <Grid item xs={12} md={4} key={plan.slug}>
                    <Paper
                      variant="outlined"
                      onClick={() => setSelectedPlan(plan.slug)}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        cursor: "pointer",
                        position: "relative",
                        transition: "all 0.2s",
                        borderColor: isSelected ? color : isActive ? alpha(color, 0.5) : "divider",
                        borderWidth: isSelected || isActive ? 2 : 1,
                        bgcolor: isSelected ? alpha(color, 0.05) : "background.paper",
                        "&:hover": {
                          borderColor: color,
                          transform: "translateY(-2px)",
                          boxShadow: `0 8px 24px ${alpha(color, 0.15)}`,
                        },
                      }}
                    >
                      {featured && (
                        <Chip
                          label="Featured"
                          size="small"
                          sx={{ position: "absolute", top: 12, right: 12, bgcolor: alpha(color, 0.15), color, fontWeight: 700, fontSize: 11 }}
                        />
                      )}
                      {(isActive || isSelected) && (
                        <Chip
                          label={isActive ? t("billing.currentBadge", "Current") : t("billing.selectPlan", "Selected")}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: isActive && featured ? 48 : 12,
                            right: 12,
                            bgcolor: alpha(color, 0.15),
                            color,
                            fontWeight: 700,
                            fontSize: 11,
                          }}
                        />
                      )}
                      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                        <Box sx={{ color }}>{icon}</Box>
                        <Typography fontWeight={700} fontSize={16}>
                          {t(`billing.${plan.slug}`, plan.name)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="baseline" spacing={0.5} mb={0.5}>
                        <Typography fontSize={32} fontWeight={800} color={color}>
                          {money(perVehicleCents, plan.currency)}
                        </Typography>
                        <Typography fontSize={13} color="text.secondary">{t("billing.perVehicleMonth")}</Typography>
                      </Stack>
                      <Typography fontSize={13} color="text.secondary" mb={2}>
                        {money(totalCents, plan.currency)}{" "}
                        {billing === "annual"
                          ? t("billing.estimatedYear")
                          : t("billing.estimatedMonth")}
                      </Typography>
                      <Divider sx={{ my: 1.5 }} />
                      <Stack spacing={1}>
                        {(plan.features ?? []).map((f) => (
                          <Stack key={f} direction="row" spacing={1} alignItems="flex-start">
                            <Box sx={{ color, mt: 0.2, flexShrink: 0 }}><FiCheck size={14} /></Box>
                            <Typography fontSize={12} color="text.secondary">
                              {t(`billing.feature.${f}`, f.replace(/_/g, " "))}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

        {/* ── AI Layer ── */}
        <Box>
          <Typography variant="h6" fontWeight={700} mb={2}>{t("billing.aiLayer", "AI Layer")}</Typography>
          <Grid container spacing={2}>
            {aiAddons.map(({ plan, icon, colorKey }) => {
              const color = colorFor(colorKey);
              const isSelected = selectedAddon === plan.slug;
              const isActive = activeAddonSlug === plan.slug;

              return (
                <Grid item xs={12} md={4} key={plan.slug}>
                  <Paper
                    variant="outlined"
                    onClick={() => setSelectedAddon(isSelected ? null : plan.slug)}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      cursor: "pointer",
                      position: "relative",
                      transition: "all 0.2s",
                      borderColor: isSelected || isActive ? color : "divider",
                      borderWidth: isSelected || isActive ? 2 : 1,
                      bgcolor: isSelected ? alpha(color, 0.05) : "background.paper",
                      "&:hover": {
                        borderColor: color,
                        transform: "translateY(-2px)",
                        boxShadow: `0 8px 24px ${alpha(color, 0.15)}`,
                      },
                    }}
                  >
                    {isActive && (
                      <Chip
                        label={t("billing.currentBadge", "Current")}
                        size="small"
                        sx={{ position: "absolute", top: 12, right: 12, bgcolor: alpha(color, 0.15), color, fontWeight: 700, fontSize: 11 }}
                      />
                    )}
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                      <Box sx={{ color }}>{icon}</Box>
                      <Typography fontWeight={700} fontSize={14}>
                        {ADDON_LABEL_KEY[plan.slug as AiAddonId]
                          ? t(`billing.${ADDON_LABEL_KEY[plan.slug as AiAddonId]}`)
                          : plan.name}
                      </Typography>
                    </Stack>
                    <Chip label={t("billing.addonOptional")} size="small" variant="outlined" sx={{ fontSize: 11, mb: 1.5 }} />
                    {/* Headline is the MONTHLY rate with the yearly figure
                        underneath, mirroring the SaaS cards above. Showing the
                        yearly total as the headline made the AI layer look an
                        order of magnitude more expensive than the plan it sits
                        under, because the two sections were quoting different
                        periods in the same type size. Both figures follow the
                        cycle toggle: price_cents_annual is itself a per-month
                        rate, so annual just swaps in the discounted rate. */}
                    <Stack direction="row" alignItems="baseline" spacing={0.5} mb={0.25}>
                      <Typography fontSize={24} fontWeight={800} color={color}>
                        {money(addonPerMonthCents(plan, billing), plan.currency)}
                      </Typography>
                      <Typography fontSize={12} color="text.secondary">
                        {t("billing.perMonth")}
                      </Typography>
                    </Stack>
                    <Typography fontSize={12} color="text.secondary" mb={1.5}>
                      {money(addonYearlyCents(plan, billing), plan.currency)}{" "}
                      {t("billing.estimatedYear")}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Stack spacing={0.75}>
                      {(plan.features ?? []).map((f) => (
                        <Stack key={f} direction="row" spacing={1} alignItems="flex-start">
                          <Box sx={{ color, mt: 0.2, flexShrink: 0 }}><FiCheck size={13} /></Box>
                          <Typography fontSize={12} color="text.secondary">
                            {t(`billing.feature.${f}`, f.replace(/_/g, " "))}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* ── Checkout summary ── */}
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Typography fontWeight={700} mb={2}>{t("billing.checkout", "Checkout summary")}</Typography>

          {collectPromoHere && (
            <Box sx={{ mb: 2, maxWidth: 420 }}>
              <PromoCodeInput
                companyId={companyId}
                planId={
                  saasPlans.find((x) => x.plan.slug === currentPlanForCheckout)?.plan.id ?? null
                }
                value={promo}
                onChange={setPromo}
                disabled={pendingAction === "checkout" || !canManageBilling}
              />
            </Box>
          )}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack spacing={0.75}>
                {[
                  {
                    label: `SaaS – ${t(`billing.${currentPlanForCheckout}`)}`,
                    value: (() => {
                      const p = saasPlans.find((x) => x.plan.slug === currentPlanForCheckout)?.plan;
                      if (!p) return "—";
                      return `${vehicleCount} × ${money(saasPerVehicleCents(p, billing), p.currency)} × ${
                        billing === "annual" ? "12" : "1"
                      } = ${money(saasTotalCents(p, billing, vehicleCount), p.currency)}`;
                    })(),
                  },
                  ...(selectedAddon
                    ? [{
                        label: t("billing.aiLayer"),
                        value: (() => {
                          const a = aiAddons.find((x) => x.plan.slug === selectedAddon)?.plan;
                          if (!a) return "—";
                          // Follows the cycle toggle — previously always annual.
                          return `${money(addonTotalCents(a, billing), a.currency)} ${
                            billing === "annual"
                              ? t("billing.billedAnnually")
                              : t("billing.billedMonthly", "Billed monthly")
                          }`;
                        })(),
                      }]
                    : []),
                  { label: t("billing.billingCycle"), value: t(`billing.${billing}`) },
                ].map(({ label, value }) => (
                  <Stack key={label} direction="row" justifyContent="space-between">
                    <Typography fontSize={13} color="text.secondary">{label}</Typography>
                    <Typography fontSize={13} fontWeight={600}>{value}</Typography>
                  </Stack>
                ))}

                {/*
                  The discount is stated as its own line rather than folded
                  into the total. A customer who applies a code and sees only a
                  smaller number has no way to check the code did what it said.
                */}
                {promo && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography fontSize={13} color="success.main">
                      {t("billing.promoApplied", "Code promo")} — {promo.code}
                    </Typography>
                    <Typography fontSize={13} fontWeight={700} color="success.main">
                      −{promo.discount_label}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
              <Stack direction={{ xs: "row", md: "column" }} spacing={1} alignItems={{ xs: "center", md: "flex-end" }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <FiExternalLink size={12} style={{ opacity: 0.5 }} />
                  <Typography variant="caption" color="text.disabled">
                    {t("billing.managedByProvider", "Managed by {{provider}}", {
                      provider:
                        PROVIDER_LABELS[billingConfig.provider] ?? billingConfig.provider,
                    })}
                  </Typography>
                </Stack>
                <CustomButtonTable
                  variant="primary"
                  textVariant="link"
                  label={t("billing.startCheckout", "Start checkout")}
                  textTransform="uppercase"
                  width="fit-content"
                  fontWeight="bold"
                  textDecoration="none"
                  iconName="plus"
                  disabled={pendingAction === "checkout" || plansLoading || !canManageBilling}
                  onClick={handleCheckout}
                />
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      {/* Cancellation confirmation � billing is destructive, so confirm first. */}
      <CustomModal
        open={confirmCancelOpen}
        title={t("billing.cancelTitle", "Cancel subscription")}
        isConfirmation
        textConfirmation={t("billing.confirmCancel", "Yes, cancel")}
        onConfirm={handleCancel}
        onCancel={() => setConfirmCancelOpen(false)}
        onClose={() => setConfirmCancelOpen(false)}
      >
        <CustomTypography
          variant="default"
          text={t(
            "billing.cancelWarning",
            "Your plan stays active until the end of the current billing period, then it won't renew. You can resume any time before it ends."
          )}
        />
      </CustomModal>
    </Sidebar>
  );
};
